import { assertDefined } from '@deltasierra/shared';
import * as React from 'react';
import styled from 'styled-components';
import { DSTextButton } from '../../Generic/Buttons/DSTextButton';

type OnClickType = () => void;

interface ReportSectionToolbarOption {
    title: string;
    onClick: OnClickType;
    active?: boolean;
}

const StyledWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
`;

const StyledTextButton = styled(DSTextButton)<{ $active?: boolean }>`
    padding: 2px;
    margin: 0 5px;
    font-size: 1.2rem;

    border-radius: 0;
    border-bottom: 2px solid transparent;

    :hover {
        background: none;
        border-bottom: 2px solid #ff4f4c;
    }

    ${({ $active }) =>
        $active
            ? `
                font-weight: bold;
            `
            : ''}
`;

/**
 * Easy to use function for creating a ReportSectionToolbar
 *
 * @param defaultActive - The default active key
 * @param items - The items for the toolbar
 * @returns - A tuple of the currentState, the Toolbar element, and the set key action
 */
export function useReportSectionToolbar<
    R extends {
        key: K;
        title?: string;
    },
    K extends string = string,
>(defaultActive: R['key'], items: ReadonlyArray<R>): [R, JSX.Element, React.Dispatch<React.SetStateAction<K>>] {
    const [active, updateActive] = React.useState(defaultActive);

    const currentItem = items.find(item => item.key === active);

    assertDefined(currentItem);

    const toolbar: ReportSectionToolbarOption[] = items.map(item => ({
        active: active === item.key,
        onClick: () => {
            updateActive(item.key);
        },
        title: item.title ?? item.key,
    }));

    return [
        currentItem,
        // eslint-disable-next-line react/jsx-key
        <ReportSectionToolbar options={toolbar} />,
        updateActive,
    ];
}

export interface ReportSectionToolbarProps {
    options: ReportSectionToolbarOption[];
}

export const ReportSectionToolbar: React.FunctionComponent<ReportSectionToolbarProps> = ({ options }) => (
    <StyledWrapper>
        {options.map(option => (
            <StyledTextButton
                $active={option.active}
                className={option.active ? undefined : 'hide-print'}
                hideArrow={true}
                key={option.title}
                onClick={option.onClick}
            >
                {option.title}
            </StyledTextButton>
        ))}
    </StyledWrapper>
);
ReportSectionToolbar.displayName = 'ReportSectionToolbar';
